import { styled } from '@linaria/react';
import { fonts, primitives, radius, semantic, size, space } from '../../theme/tokens';
import { EntityImage } from '../EntityImage';

const SMALL_MOBILE = '(max-width: 399px)';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-400']};
  width: 100%;

  > * {
    transition: opacity 0.2s ease;
  }

  &[data-removing] > *:not([data-remove-row]) {
    opacity: 0.45;
  }
`;

export const TopRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${space['space-600']};
  width: 100%;
`;

export const TopRowLink = styled.a`
  position: absolute;
  inset: 0;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  border-radius: ${size.radius['radius-xs']};

  &:hover ~ * .basket-card-title,
  &:focus-visible ~ * .basket-card-title {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid currentColor;
    outline-offset: ${space['space-050']};
  }
`;

export const Thumbnail = styled.div`
  flex-shrink: 0;
  width: ${space['space-2400']};
  height: ${space['space-2400']};
  border-radius: ${radius('image', 'radius-xs')};
  overflow: hidden;
  background: ${semantic.bg.surfaceSubtle};

  @media ${SMALL_MOBILE} {
    width: ${space['space-1600']};
    height: ${space['space-1600']};
  }
`;

export const ThumbnailImage = styled(EntityImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
  text-align: center;
  padding: ${space['space-200']};
`;

export const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  gap: ${space['space-600']};
  align-items: flex-start;

  @media ${SMALL_MOBILE} {
    gap: ${space['space-400']};
  }
`;

export const TextColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${space['space-050']};
`;

export const Title = styled.p`
  margin: 0;
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.normal};
  color: ${semantic.text.primary};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
`;

export const MetaList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const MetaItem = styled.li`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.secondary};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
`;

export const MetaItemLabel = styled.span``;

const REMOVE_GLYPH_DIAMETER = '14px';
const REMOVE_GLYPH_CROSS = '6px';

export const MetaItemRemoveButton = styled.button`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  margin-left: ${space['space-200']};
  width: ${REMOVE_GLYPH_DIAMETER};
  height: ${REMOVE_GLYPH_DIAMETER};
  padding: 0;
  border: none;
  border-radius: ${radius('button', 'radius-full')};
  background: ${semantic.bg.primary};
  color: ${semantic.icon.inverse};
  cursor: pointer;
  line-height: 1;

  > svg {
    width: ${REMOVE_GLYPH_CROSS};
    height: ${REMOVE_GLYPH_CROSS};
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${space['space-600']};
    height: ${space['space-500']};
    transform: translate(-50%, -50%);
  }

  &:hover,
  &:active,
  &:focus-visible {
    background: color-mix(
      in srgb,
      ${semantic.bg.primary} 60%,
      ${primitives.color.white['color-white-alpha-1000']}
    );
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &:disabled {
    background: ${semantic.bg.disabled};
    color: ${semantic.icon.disabled};
    cursor: default;
  }

  @media (pointer: coarse) {
    &::after {
      width: ${space['space-800']};
    }
  }
`;

export const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  flex-shrink: 0;
  gap: ${space['space-0']};
`;

export const SubPriceRow = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.normal};
  color: ${semantic.text.primary};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
`;

export const RemoveRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: ${space['space-500']};
`;

export const RemoveButton = styled.button`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.normal};
  color: ${semantic.text.primary};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  &:disabled {
    color: ${semantic.text.disabled};
    cursor: default;
    text-decoration: none;
  }
`;

export const RemoveStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space['space-200']};
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.normal};
  color: ${semantic.text.secondary};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};
`;
